import React, { useMemo, useState } from 'react';

import { Box, Skeleton, Typography } from '@mui/material';
import CurrencyFormatter from '@apigrowfinance/pbolt-currency-formatter';
import { Panel, PanelContent, PanelTitle } from '@apigrowfinance/pbolt-panel';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import getEnumTranslation from '@utils/getEnumTranslation';

export type DelinquencyPanelProps = {
    totalOverDue: number;
    isLoading: boolean;
    currencyCode: string;
    accountId: string;
};

const DelinquencyPanel = ({
    totalOverDue,
    isLoading,
    currencyCode,
    accountId,
}: DelinquencyPanelProps) => {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);

    const ariaLabel = useMemo(
        () =>
            formatMessage({
                id: 'DelinquencyPanel.ariaLabel',
                defaultMessage: 'View delinquency',
            }),
        [formatMessage],
    );

    const viewDelinquency = () => navigate(`/${accountId}/delinquency`);

    return (
        <Panel
            onClick={isLoading ? null : viewDelinquency}
            isHovered={setIsHovered}
            ariaLabel={ariaLabel}
            evenSidePadding={false}>
            <PanelTitle
                hovered={isHovered}
                addPadding
                arrowLabel={getEnumTranslation('viewAll')}
            />
            <PanelContent>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                    <Typography
                        variant="overline"
                        data-testid="delinquency-title">
                        {isLoading ? (
                            <Skeleton width={120} data-testid="title-loader" />
                        ) : (
                            <FormattedMessage
                                id="DelinquencyPanel.DelinquencyPanel"
                                defaultMessage="Total overdue amount"
                            />
                        )}
                    </Typography>
                    <Typography
                        variant="h2"
                        data-testid="delinquency-value"
                        sx={{ fontWeight: 600 }}>
                        {isLoading ? (
                            <Skeleton
                                width={160}
                                data-testid="delinquency-value-loader"
                            />
                        ) : (
                            <CurrencyFormatter
                                variant="error"
                                amount={totalOverDue}
                                currency={currencyCode}
                            />
                        )}
                    </Typography>
                </Box>
            </PanelContent>
        </Panel>
    );
};

export default DelinquencyPanel;
